import { IconButton } from "@itk/mui-components";
import { FormField, required } from "@itk/react-form";
import { Grid, InputAdornment, Skeleton, Stack, TextField } from "@mui/material";
import { genericTranslationsK, useGenericTranslationsT } from "src/translation-hooks/generic-translations";
import { ComponentType, useEffect, useMemo, useState } from "react";
import { DekoxIcon, ViewMode } from "src/components";
import { FormListItemPictureAutocomplete } from "src/components/form-list-item-picture-autocomplete";
import { ITEM_CARD_IMAGE_HEIGHT } from "../../add-edit-configuration-dialog-types";
import { ItemCard } from "../item-card";
import { connect } from "react-redux";
import { addEditConfigurationDialogActions } from "../../add-edit-configuration-dialog-store";
import { RootState } from "src/store";
import { useParams } from "react-router";
import { useConfiguratorPageT } from "src/translation-hooks/configurator-page";
import { getProductFilterValidities } from "./product-filter-validity-utils";

const mapStateToProps = (state: RootState) => ({
  productsState: state.pageReducer.combinedConfiguratorReducer.addEditConfigurationDialogReducer.productsState,
  productPictures: state.pageReducer.combinedConfiguratorReducer.addEditConfigurationDialogReducer.productPictures,
  attributesState: state.pageReducer.combinedConfiguratorReducer.addEditConfigurationDialogReducer.attributesState,
  formData: state.pageReducer.combinedConfiguratorReducer.addEditConfigurationDialogReducer.formData,
  listItemsState: state.generalReducer.formListItemPictureAutocompleteReducer.listItemsState
});

const ownActions = {
  setAddEditConfigurationDialog: addEditConfigurationDialogActions.setAddEditConfigurationDialog,
  getProductsRequest: addEditConfigurationDialogActions.getProductsRequest,
  getAttributesRequest: addEditConfigurationDialogActions.getAttributesRequest,
  setFormData: addEditConfigurationDialogActions.setFormData
};

type OwnProps = { onSave?: (productId: string) => void };
type Props = typeof ownActions & ReturnType<typeof mapStateToProps> & OwnProps;

const SelectProductBase = (props: Props) => {
  const { setAddEditConfigurationDialog, getProductsRequest, getAttributesRequest, productsState, attributesState, formData, setFormData, productPictures, listItemsState, onSave } = props;

  const genericTranslationsT = useGenericTranslationsT();
  const configuratorPageT = useConfiguratorPageT();

  const { projectId } = useParams<{ projectId: string }>();

  const [searchText, setSearchText] = useState("");

  const [hasRequestedAttributesThisMount, setHasRequestedAttributesThisMount] = useState(false);

  const needsAttributeFilters = !!formData?.productsFilter?.parentProjectLineItemId && !!formData?.productsFilter?.productTypes?.length;

  useEffect(() => {
    const productTypeId = formData?.productsFilter?.productTypes?.[0];
    if (!needsAttributeFilters || !productTypeId) {
      setHasRequestedAttributesThisMount(true);
      return;
    }
    getAttributesRequest(productTypeId);
    setHasRequestedAttributesThisMount(true);
  }, [needsAttributeFilters, formData.productsFilter?.productTypes]);

  const filterValidities = useMemo(
    () => (needsAttributeFilters ? getProductFilterValidities(attributesState, formData.productsFilter?.attributeFilters) : []),
    [needsAttributeFilters, attributesState, formData.productsFilter?.attributeFilters]
  );
  const isFilterValid = filterValidities.every(v => v.isValid);

  const areListItemsReady = filterValidities.every(v => !v.attribute.listId || listItemsState[v.attribute.listId]?.loading === "has-loaded");
  const isPrerequisitesLoading = !hasRequestedAttributesThisMount || (needsAttributeFilters && attributesState.loading !== "has-loaded") || !areListItemsReady;
  const isRequiredFilterPending = filterValidities.some(v => v.attribute.isRequired && !v.selectedValue);
  const [hasRequestedProductsThisMount, setHasRequestedProductsThisMount] = useState(false);

  const isLoading = isPrerequisitesLoading || (!isRequiredFilterPending && (!hasRequestedProductsThisMount || productsState.loading === "is-loading"));

  useEffect(() => {
    const currentAttributeFilters = formData?.productsFilter?.attributeFilters;
    if (!currentAttributeFilters?.length || attributesState.loading !== "has-loaded") return;
    const matchedIds = new Set(attributesState.data.map(attribute => attribute.attributeId));
    const filtered = currentAttributeFilters.filter(filter => filter.attributeId !== undefined && matchedIds.has(filter.attributeId));
    if (filtered.length !== currentAttributeFilters.length) {
      setFormData({ productsFilter: { attributeFilters: filtered } });
    }
  }, [attributesState]);

  useEffect(() => {
    if (!projectId || !formData?.productsFilter?.systemPackageId) return;
    if (isPrerequisitesLoading) return;
    if (isRequiredFilterPending) return;
    getProductsRequest(projectId, { ...(formData.productsFilter || {}) });
    setHasRequestedProductsThisMount(true);
  }, [formData.productsFilter?.systemPackageId, formData.productsFilter?.attributeFilters, isPrerequisitesLoading, isRequiredFilterPending]);

  const products = useMemo(() => {
    if (productsState.loading === "has-loaded") {
      return productsState.data.sort().filter(x => {
        return (
          x.name?.lowerCaseIncludes(searchText) ||
          x.productNumber?.lowerCaseIncludes(searchText) ||
          x.description2?.lowerCaseIncludes(searchText) ||
          x.summary?.lowerCaseIncludes(searchText) ||
          x.productTypeName?.lowerCaseIncludes(searchText)
        );
      });
    }
    return [];
  }, [searchText, productsState]);

  return (
    <Stack gap={2}>
      <Stack direction="row" alignItems="flex-start" gap={2}>
        <TextField
          autoFocus
          fullWidth
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          placeholder={genericTranslationsT.search}
          autoComplete="search"
          sx={{
            "& .MuiInputBase-input": {
              padding: 0
            },
            "& .MuiInputBase-root .MuiInputBase-input": {
              height: 40
            }
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <DekoxIcon icon="search" color="primary" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {searchText && (
                    <IconButton k={genericTranslationsK.dynamicValue} onClick={() => setSearchText("")}>
                      <DekoxIcon fontSize="small" icon="close" />
                    </IconButton>
                  )}
                </InputAdornment>
              )
            }
          }}
        />
        <ViewMode value={formData?.viewMode || "FULL"} onChange={value => setFormData({ viewMode: value })} />
      </Stack>
      {filterValidities.map(({ attribute, selectedValue, error }) => {
        if (!attribute.listId) return null;

        const filterListValueField: FormField<string> = {
          formFieldState: {
            value: selectedValue,
            error,
            componentError: undefined,
            touched: false,
            submitting: false,
            validating: false,
            include: true
          },
          formFieldController: {
            onStateChange: state => {
              if (!("value" in state)) return;
              const value = state.value;
              const otherFilters = formData.productsFilter?.attributeFilters?.filter(filter => filter.attributeId !== attribute.attributeId) ?? [];
              setFormData({ productsFilter: { attributeFilters: value ? [...otherFilters, { attributeId: attribute.attributeId, value }] : otherFilters } });
            },
            validators: attribute.isRequired ? [required] : undefined
          }
        };

        return (
          <FormListItemPictureAutocomplete
            key={attribute.attributeId}
            listId={attribute.listId}
            label={<>{attribute.description}</>}
            t={genericTranslationsT}
            k={genericTranslationsK}
            field="dynamicValue"
            size="small"
            sx={{ "& .MuiAutocomplete-input": { fontWeight: 400 } }}
            slotProps={{ popper: { sx: { "& .MuiAutocomplete-option": { fontWeight: 400 } } } }}
            {...filterListValueField}
          />
        );
      })}
      <Grid container spacing={2}>
        {isLoading && (
          <>
            {Array(4)
              .fill(undefined)
              .map((_, index) => (
                <Grid key={index} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                  <Skeleton height={formData.viewMode === "FULL" ? ITEM_CARD_IMAGE_HEIGHT : 62} />
                </Grid>
              ))}
          </>
        )}
        {!isLoading && productsState.loading === "has-loaded" && products.length === 0 && (
          <Grid size={{ xs: 12 }}>
            <Stack alignItems="center" py={4}>
              {configuratorPageT.noProductsFound}
            </Stack>
          </Grid>
        )}
        {!isLoading && productsState.loading === "has-loaded" && (
          <>
            {products.map(product => {
              const picture = productPictures.find(productPicture => productPicture.productPictureId === product.primaryProductPictureId)?.picture;

              return (
                <Grid key={product.productId} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                  <ItemCard
                    key={product.productId}
                    id={product.productId}
                    name={product.name}
                    productNumber={product.productNumber}
                    type={product.productTypeName}
                    image={picture}
                    isSelected={formData.selectedProductId === product.productId}
                    onSelect={() => {
                      setFormData({ selectedProductId: product.productId });
                    }}
                    onDoubleClick={() => isFilterValid && onSave?.(product.productId)}
                    onInfoIconClick={() => {
                      setAddEditConfigurationDialog({ step: "PRODUCT-DETAILS", productId: product.productId });
                    }}
                    isMinimal={formData.viewMode === "MINIMAL"}
                  />
                </Grid>
              );
            })}
          </>
        )}
      </Grid>
    </Stack>
  );
};

export const SelectProduct = connect(mapStateToProps, ownActions)(SelectProductBase) as unknown as ComponentType<OwnProps>;
